import {MiniSignal, random, texture, padNum} from "p3";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";
import Actor from "../Actor";

export default class Enemy extends Actor {
    /**
     * @param {string} id
     */
    constructor(id) {
        super();

        this._id = id;
        this._state = null;
        this._initialPosition = null;
        this._damage = 1;
        this._health = 3;

        this.linearDamping = 6;

        this._smokePuff = null;
        this.target = null;
        this.hostile = true;
        this.updateFSM = true;
        this.alwaysAggro = false;
        this.neighbours = [];

        this.respawnDelayMin = 60;
        this.respawnDelayMax = 120;

        this._respawnDelay = 0;
        this._respawnTime = 0;
        this._canRespawn = false;
        this._isRespawning = false;

        this.collisionDynamic = true;
        this.allowDamagePushback = true;

        this.signals.attack = new MiniSignal();
        this.signals.dead = new MiniSignal();
        this.signals.respawn = new MiniSignal();

        this._damageEffectFilter = null;
        this._damageEffectTween = null;
    }

    init() {
        super.init();

        this._createSmokePuff();

        this._initialPosition = new PIXI.Point(this.x, this.y);
        this._damageEffectFilter = new P3.filters.AdditiveFilter(0, 3);

        this.damage = 1;
    }

    onViewAdded() {
        super.onViewAdded();

        this.state = definitions.ENEMY_STATE.IDLE;
    }

    destroy() {
        this.removeDamageEffect();
        this._smokePuff.destroy();

        super.destroy();
    }

    update() {
        super.update();

        if (this._smokePuff) {
            this._smokePuff.update(window.dt);
            if (!this._smokePuff.playing) {
                this._smokePuff.visible = false;
            }
        }

        if (!this.isRespawning) {
            if (this.updateFSM) {
                this.isStateValid(this._state) && this.onStateUpdate(this._state);
            }
        } else {
            this.updateRespawn();
        }

        if (this.alwaysAggro) {
            if (this._state === definitions.ENEMY_STATE.IDLE ||
                this._state === definitions.ENEMY_STATE.WANDER ||
                this._state === definitions.ENEMY_STATE.RETREAT) {
                this.state = definitions.ENEMY_STATE.CHASE;
            }
        }
    }

    updateRespawn() {
        if (this._canRespawn) {
            if (this._respawnTime > this._respawnDelay) {
                this.doRespawn();
            } else {
                this._respawnTime += window.dt;
            }
        }
    }

    _createSmokePuff() {
        let frames = [];
        for (let i = 0; i < 15; ++i) {
            frames[i] = texture("smoke_puff_fx_" + padNum(i, 2));
        }
        const sequence = new P3.MovieClipSequence(frames, "smoke_puff");

        this._smokePuff = new P3.MovieClip(sequence, "smoke_puff", false);
        this._smokePuff.anchor.set(0.5);
        this._smokePuff.position.set(0, -70);
        this.addChild(this._smokePuff);

        this._smokePuff.visible = false;
    }

    /**
     * @param {number} amount
     */
    takeDamage(amount) {
        if (this.isDead) return false;

        this.damageEffect().then();

        this.health -= amount;
        this.onTakeDamage(amount);
        
        this.isDead && this.onDead();

        this.state = this.isDead ? definitions.ENEMY_STATE.DEAD : definitions.ENEMY_STATE.TAKE_DAMAGE;
        return this.isDead;
    }

    /**
     * @return {Promise}
     */
    damageEffect() {
        return new Promise((resolve) => {
            this.removeDamageEffect();

            this._damageEffectFilter.blendStrength = 0.3;
            this._damageEffectTween = GameScene.active.addTween(gsap.to(this._damageEffectFilter, 0.24, {
                blendStrength: 0,
                ease: Power2.easeOut,
                onStart: () => {
                    this.filters = [this._damageEffectFilter]
                },
                onComplete: () => {
                    this.filters = [];
                    resolve();
                }
            }));
        });
    }

    removeDamageEffect() {
        if (this._damageEffectTween) {
            GameScene.active.removeTween(this._damageEffectTween, true);
            this._damageEffectTween = null;
        }

        this._damageEffectFilter.blendStrength = 0;
        this.filters = [];
    }

    startRespawn() {
        this._respawnDelay = random(this.respawnDelayMin, this.respawnDelayMax);
        this._respawnTime = 0;
        this._isRespawning = true;
    }

    doRespawn() {
        this._respawnTime = 0;
        this._isRespawning = false;

        this._health = 3;
        this.position = this._initialPosition;

        this.onRespawn();
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        super.onCollisionEntity(entity, v);
    }

    /**
     * @param {Tile} tile
     * @param {P3.Vector2} v
     */
    onCollisionTile(tile, v) {
        super.onCollisionTile(tile, v);
    }

    /**
     * @param {number} state
     */
    onStateEnter(state) {
        //console.log("enter:", state);
    }

    /**
     * @param {number} state
     */
    onStateUpdate(state) {
        //console.log("update:", state);
    }

    /**
     * @param {number} state
     */
    onStateExit(state) {
        //console.log("exit:", state);
    }

    /**
     * @param {number} state
     * @return {boolean}
     */
    isStateValid(state) {
        return typeof state === "number" && state >= 0;
    }

    /**
     * @param {number} damage
     */
    onTakeDamage(damage) {
    }

    onDead() {
        this._smokePuff.visible = true;
        this._smokePuff.play(false);

        this.signals.dead.dispatch(this);

        this.canRespawn && this.startRespawn();
    }

    onRespawn() {
        this.state = definitions.ENEMY_STATE.WANDER;

        this.signals.respawn.dispatch(this);
    }

    /**
     * @return {string}
     */
    get id() {
        return this._id;
    }

    /**
     * @return {number}
     */
    get state() {
        return this._state;
    }

    /**
     * @param {number} value
     */
    set state(value) {
        const previous = this._state;
        this._state = value;

        this.isStateValid(previous) && this.onStateExit(previous);
        this.isStateValid(this._state) && this.onStateEnter(this._state);
    }

    /**
     * @return {PIXI.Point}
     */
    get initialPosition() {
        return this._initialPosition;
    }

    /**
     * @return {number}
     */
    get damage() {
        return this._damage;
    }

    /**
     * @param {number} value
     */
    set damage(value) {
        this._damage = Math.max(0, value);
    }

    /**
     * @return {number}
     */
    get health() {
        return this._health;
    }

    /**
     * @param {number} value
     */
    set health(value) {
        this._health = Math.max(0, value);
    }

    /**
     * @return {boolean}
     */
    get isDead() {
        return this._health <= 0;
    }

    /**
     * @return {boolean}
     */
    get canRespawn() {
        return this._canRespawn;
    }

    /**
     * @param {boolean} value
     */
    set canRespawn(value) {
        this._canRespawn = value;

        if (value) {
            this.isDead && this.startRespawn();
        }
    }

    /**
     * @return {boolean}
     */
    get isRespawning() {
        return this._isRespawning;
    }
}